import { apiClient, type ManualCommandConfig } from '@/services/http/client'
import { object, id, digest, type Data } from './workflow'
import { evidence } from './sourceReview'

const root = '/client-identity-reviews'
const manual = { manualCommand: true } as ManualCommandConfig
export type Operation = 'merge' | 'split' | 'dispute' | 'resolve_dispute' | 'correction'
export interface Root {
  client_id: string
  client_type: string
  status: string
  group_id: string
  subject_id: string | null
}
export interface Context {
  roots: Root[]
  required_client_ids: string[]
  snapshot_sha256: string
  authority_granted: false
}
export interface Submission extends Data {
  case_id: string
  client_ids: string[]
  operation: Operation
  partitions: string[][]
  expected_snapshot_sha256: string
  evidence_reference: string
  evidence_sha256: string
  profile_version_id: string | null
  expires_at: string
}
export interface Decision extends Data {
  case_id: string
  decision: 'approve' | 'reject'
  expected_command_sha256: string
  evidence_sha256: string
}
export interface Receipt {
  case_id: string
  command: Submission
  command_sha256: string
  submitter_user_id: string
  state: 'pending' | 'expired' | 'approve' | 'reject'
  expires_at: string
  decision: Data | null
}
export interface Intent {
  case_id: string
  action: 'submit' | 'decide'
  command_sha256: string
}
const key = (account: string) => 'txh.identity-review.' + id(account)
function hash(v: unknown): string {
  if (typeof v !== 'string' || !digest.test(v)) throw new Error('Original fingerprint unavailable')
  return v
}
export function identifiers(value: string): string[] {
  const result = value
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(id)
    .sort()
  if (!result.length || result.length > 16 || new Set(result).size !== result.length)
    throw new Error('Enter 1 to 16 distinct original client references')
  return result
}
export function submission(value: unknown): Submission {
  const row = object(value)
  if (
    !['merge', 'split', 'dispute', 'resolve_dispute', 'correction'].includes(
      String(row.operation)
    ) ||
    !Array.isArray(row.client_ids) ||
    !Array.isArray(row.partitions)
  )
    throw new Error('Original identity command unavailable')
  const clients = identifiers(row.client_ids.join(','))
  const partitions = row.partitions
    .map(group => {
      if (!Array.isArray(group)) throw new Error('Enter the exact identity partitions')
      return identifiers(group.join(','))
    })
    .sort((a, b) => a.join(',').localeCompare(b.join(',')))
  if (row.operation === 'merge' || row.operation === 'split') {
    if (
      (row.operation === 'merge' && partitions.length !== 1) ||
      (row.operation === 'split' && partitions.length < 2) ||
      partitions.flat().sort().join(',') !== clients.join(',')
    )
      throw new Error('Partitions must cover every selected client exactly once')
  } else if (partitions.length) throw new Error('This decision cannot regroup identities')
  if (typeof row.expires_at !== 'string' || !Number.isFinite(Date.parse(row.expires_at)))
    throw new Error('Finite review expiry required')
  if ((row.operation === 'correction') !== (row.profile_version_id != null))
    throw new Error('Use the approved profile correction reference')
  return {
    case_id: id(row.case_id),
    client_ids: clients,
    operation: row.operation as Operation,
    partitions,
    expected_snapshot_sha256: hash(row.expected_snapshot_sha256),
    evidence_reference: evidence(row.evidence_reference),
    evidence_sha256: hash(row.evidence_sha256),
    profile_version_id: row.profile_version_id == null ? null : id(row.profile_version_id),
    expires_at: new Date(row.expires_at).toISOString(),
  }
}
export function context(value: unknown): Context {
  const row = object(value)
  if (
    row.authority_granted !== false ||
    !Array.isArray(row.roots) ||
    !row.roots.length ||
    row.roots.length > 16 ||
    !Array.isArray(row.required_client_ids)
  )
    throw new Error('Current identity context unavailable')
  const roots = row.roots.map(v => {
    const r = object(v)
    if (
      !['individual', 'business', 'huf'].includes(String(r.client_type)) ||
      !['unclaimed', 'claimed', 'disputed', 'archived'].includes(String(r.status))
    )
      throw new Error('Original identity root unavailable')
    hash(r.profile_sha256)
    hash(r.aliases_sha256)
    return {
      client_id: id(r.client_id),
      client_type: String(r.client_type),
      status: String(r.status),
      group_id: id(r.group_id),
      subject_id: r.subject_id === null ? null : id(r.subject_id),
    }
  })
  if (new Set(roots.map(r => r.client_id)).size !== roots.length)
    throw new Error('Distinct identity roots required')
  return {
    roots,
    required_client_ids: identifiers(row.required_client_ids.join(',')),
    snapshot_sha256: hash(row.snapshot_sha256),
    authority_granted: false,
  }
}
export function receipt(value: unknown, expected: string): Receipt {
  const r = object(value),
    command = submission(r.command)
  if (
    id(r.case_id) !== id(expected) ||
    command.case_id !== id(expected) ||
    r.authority_granted !== false ||
    !['pending', 'expired', 'approve', 'reject'].includes(String(r.state))
  )
    throw new Error('Original identity receipt unavailable')
  if (
    typeof r.expires_at !== 'string' ||
    !Number.isFinite(Date.parse(r.expires_at)) ||
    Date.parse(r.expires_at) !== Date.parse(command.expires_at) ||
    ['approve', 'reject'].includes(String(r.state)) !== (r.decision != null)
  )
    throw new Error('Exact original decision state unavailable')
  if (r.decision != null) {
    const d = object(r.decision)
    if (
      id(d.case_id) !== id(expected) ||
      d.authority_granted !== false ||
      d.decision !== r.state ||
      !Array.isArray(d.assignments) ||
      d.assignments.length > 16
    )
      throw new Error('Original identity decision unavailable')
  }
  return {
    case_id: id(r.case_id),
    command,
    command_sha256: hash(r.command_sha256),
    submitter_user_id: id(r.submitter_user_id),
    state: r.state as Receipt['state'],
    expires_at: String(r.expires_at),
    decision: r.decision == null ? null : object(r.decision),
  }
}
function canonical(value: unknown): string {
  if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']'
  if (value && typeof value === 'object')
    return (
      '{' +
      Object.keys(value)
        .sort()
        .map(k => JSON.stringify(k) + ':' + canonical((value as Data)[k]))
        .join(',') +
      '}'
    )
  return JSON.stringify(value)
}
export async function commandHash(value: Data): Promise<string> {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical(value)))
  return Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('')
}
export function pending(account: string): Intent | null {
  const raw = localStorage.getItem(key(account))
  if (!raw) return null
  const row = object(JSON.parse(raw))
  if (
    Object.keys(row).sort().join(',') !== 'action,case_id,command_sha256' ||
    !['submit', 'decide'].includes(String(row.action))
  )
    throw new Error('Recover the saved original identity intent')
  return {
    case_id: id(row.case_id),
    action: row.action as Intent['action'],
    command_sha256: hash(row.command_sha256),
  }
}
export function save(account: string, intent: Intent) {
  localStorage.setItem(key(account), JSON.stringify(intent))
}
export function clear(account: string) {
  localStorage.removeItem(key(account))
}
export async function observe(case_id: string, client_ids: string[]) {
  const result = context(
    (
      await apiClient.post(
        root + '/context',
        { case_id: id(case_id), client_ids: identifiers(client_ids.join(',')) },
        manual
      )
    ).data
  )
  if (
    result.roots
      .map(r => r.client_id)
      .sort()
      .join(',') !== [...client_ids].sort().join(',')
  )
    throw new Error('Observed identities differ')
  return result
}
export async function inspect(reference: string) {
  return receipt((await apiClient.get(root + '/' + id(reference), manual)).data, reference)
}
export async function submit(account: string, body: Submission) {
  const value = submission(body),
    command_sha256 = await commandHash(value)
  const original = pending(account)
  if (
    original &&
    (original.case_id !== value.case_id ||
      original.action !== 'submit' ||
      original.command_sha256 !== command_sha256)
  )
    throw new Error('Recover the original command before changing it')
  save(account, { case_id: value.case_id, action: 'submit', command_sha256 })
  const acknowledged = object((await apiClient.post(root + '/submissions', value, manual)).data)
  if (id(acknowledged.case_id) !== value.case_id || acknowledged.authority_granted !== false)
    throw new Error('Original submission acknowledgment unavailable')
}
export async function decide(account: string, body: Decision) {
  id(body.case_id)
  hash(body.expected_command_sha256)
  hash(body.evidence_sha256)
  const command_sha256 = await commandHash(body),
    original = pending(account)
  if (
    original &&
    (original.case_id !== body.case_id ||
      original.action !== 'decide' ||
      original.command_sha256 !== command_sha256)
  )
    throw new Error('Re-enter the exact original decision for recovery')
  save(account, { case_id: body.case_id, action: 'decide', command_sha256 })
  await apiClient.post(root + '/' + body.case_id + '/decisions', body, manual)
}
